import {EditorState, Compartment, StateEffect} from '@codemirror/state'
import {dropCursor, EditorView, placeholder as placeholderExtension, type ViewUpdate} from '@codemirror/view'
import {type RefObject, useCallback, useEffect, useMemo, useRef} from 'react'

import {basicSetup} from '../extensions/basic-setup'
import {getTheme} from '../themes/github-theme'
import type {CodeMirrorProps} from '../CodeMirror'
import {spacingControls} from '../extensions/spacing-controls'
import {fileUploadListener} from '../extensions/file-upload'
import {secretDetectedListener} from '../extensions/secret-detected'
import {dataHpc} from '../extensions/data-hpc'
import {scrollerAttributes} from '../extensions/scroller-attributes'
import {readOnly, externalUpdate} from '../extensions/readonly'
import {focusTrapToggle} from '../extensions/focus-trap-toggle'
import {computeMinimalChange} from '../utils'
import {loadLanguage} from './load-language'
import {selectionListener} from '../extensions/selection-listener'
import {
  addSelectionAttachmentGutters,
  addSelectionAttachmentLines,
  clearSelectionAttachments,
  selectionAttachmentMarkField,
  selectionAttachmentGutterField,
  selectionAttachmentLineField,
} from '../decorations/selection-attachment-decorations'

export interface UseCodeMirror extends CodeMirrorProps {
  parentRef: RefObject<HTMLElement | null>
}

export function useCodeMirror({
  extensions = [],
  fileName = '',
  onChange,
  spacing,
  isHpc,
  isReadOnly,
  enableFileUpload,
  parentRef,
  placeholder = 'Enter file contents here',
  ariaLabelledBy,
  value,
  width = '',
  height = '',
  minHeight = '',
  hideHelpUntilFocus,
  hideHelp,
  onCreateEditor,
  onDestroyEditor,
  selectionListenerOptions,
  selectionAttachmentRange,
}: UseCodeMirror) {
  const viewRef = useRef<EditorView | undefined>(undefined)
  const languageCompartment = useMemo(() => {
    return new Compartment()
  }, [])

  const autoCompleteCompartment = useMemo(() => {
    return new Compartment()
  }, [])

  const themeCompartment = useMemo(() => {
    return new Compartment()
  }, [])

  const internalOnChange = useCallback(
    (update: ViewUpdate) => {
      if (update.docChanged) {
        onChange(update.state.doc.toString() || '')
      }
    },
    [onChange],
  )

  const updateListener = useMemo(() => EditorView.updateListener.of(internalOnChange), [internalOnChange])

  const editorSizingTheme = useMemo(
    () =>
      EditorView.theme({
        '&': {
          height,
          minHeight,
          width,
        },
      }),
    [height, minHeight, width],
  )

  const managedExtensions = useMemo(() => {
    const exts = [
      ...basicSetup,
      updateListener,
      editorSizingTheme,
      placeholderExtension(placeholder),
      ...spacingControls(spacing),
      secretDetectedListener(),
      languageCompartment.of([]),
      autoCompleteCompartment.of([]),
      themeCompartment.of([getTheme()]),
      scrollerAttributes(ariaLabelledBy),
      EditorView.contentAttributes.of({
        'aria-labelledby': `${ariaLabelledBy} focus-trap-help-panel`,
      }),
      EditorView.editorAttributes.of({
        class: hideHelpUntilFocus ? 'hide-help-until-focus' : '',
      }),
      EditorView.editorAttributes.of({
        class: hideHelp ? 'hide-help' : '',
      }),
    ]

    if (enableFileUpload) {
      exts.push(dropCursor())
      exts.push(fileUploadListener())
    }

    if (isHpc) {
      exts.push(dataHpc())
    }

    if (isReadOnly) {
      exts.push(readOnly())
    } else {
      exts.push(focusTrapToggle(hideHelp))
    }

    if (selectionListenerOptions) {
      exts.push(selectionListener(selectionListenerOptions))
    }

    // Only enable decorated selection if consumer opted in (undefined disables; null means enabled but cleared)
    if (selectionAttachmentRange !== undefined) {
      exts.push(selectionAttachmentMarkField, selectionAttachmentGutterField, selectionAttachmentLineField)
    }

    exts.push(...extensions)

    return exts
  }, [
    updateListener,
    editorSizingTheme,
    placeholder,
    spacing,
    languageCompartment,
    autoCompleteCompartment,
    themeCompartment,
    ariaLabelledBy,
    hideHelpUntilFocus,
    hideHelp,
    enableFileUpload,
    isHpc,
    isReadOnly,
    selectionListenerOptions,
    selectionAttachmentRange,
    extensions,
  ])

  useEffect(() => {
    const parent = parentRef.current
    if (!parent) return

    const state = EditorState.create({
      doc: value,
      extensions: managedExtensions,
    })

    const view = new EditorView({
      state,
      parent,
    })

    onCreateEditor?.(view)

    viewRef.current = view

    return () => {
      view.destroy()

      onDestroyEditor?.(view)
    }
    // right now - only run once to initialize
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (viewRef.current) {
      viewRef.current.dispatch({effects: autoCompleteCompartment.reconfigure([])}) // reset autocomplete
      viewRef.current.dispatch({effects: StateEffect.reconfigure.of(managedExtensions)})
    }
  }, [autoCompleteCompartment, managedExtensions])

  // update the value if it changes externally
  useEffect(() => {
    if (viewRef.current && value !== undefined) {
      const currentValue = viewRef.current.state.doc.toString() || ''
      const newValue = value || ''

      if (newValue !== currentValue) {
        // Calculate minimal change to avoid scroll jitter
        const changes = computeMinimalChange(currentValue, newValue)

        if (changes.length > 0) {
          viewRef.current.dispatch({
            changes,
            annotations: [externalUpdate.of(true)],
          })
        }
      }
    }
  }, [value])

  // Apply decorated selection effect when range changes
  useEffect(() => {
    if (viewRef.current && selectionAttachmentRange !== undefined) {
      if (selectionAttachmentRange === null) {
        // Clear all decorations
        viewRef.current.dispatch({effects: clearSelectionAttachments.of()})
      } else {
        // Apply all line and gutter decorations for the range
        viewRef.current.dispatch({
          effects: [
            addSelectionAttachmentGutters.of(selectionAttachmentRange),
            addSelectionAttachmentLines.of(selectionAttachmentRange),
          ],
        })
      }
    }
  }, [selectionAttachmentRange])

  useEffect(() => {
    loadLanguage({
      autoCompleteCompartment,
      fileName,
      languageCompartment,
      managedExtensions,
      themeCompartment,
      view: viewRef.current,
    })
  }, [fileName, languageCompartment, autoCompleteCompartment, managedExtensions, themeCompartment])

  return viewRef
}
